import { api } from '../console/api.ts';
import { empty, field, pageHead, pill } from '../console/components.ts';
import { ApiError, InputError } from '../console/errors.ts';
import { date, esc, fmt, icon, lineName, type Money, ROLE, when } from '../console/format.ts';
import { type Actions, current, render } from '../console/router.ts';
import { has } from '../console/session.ts';
import { $, act, textInput } from '../console/ui.ts';
import { REQUIRED_HINT, selectControl, textControl } from '../controls.ts';

/**
 * «Equipo de la agencia» (#313, ADR-0039): the agency's people — role, SIB code, producer codes
 * per carrier, status (active, invited, removed), policies in force and annual premium of their
 * portfolio — filtered by role and status. The direction and the administration invite a person
 * with their role, change a role and give the baja with its reason; the agency manager reads it.
 * Each person's profile shows their contact (DPI and NIT masked), SIB, codes, supervisor, agreed
 * terms with the date they hold from, portfolio and production, the link to their onboarding and
 * the team's log about them. Every change is confirmed on this screen, never a browser dialog.
 */

export type TeamPerson = {
  memberId: string;
  name: string;
  email: string;
  role: string;
  status: 'ACTIVE' | 'INVITED' | 'REMOVED' | string;
  agentId: string | null;
  supervisorId: string | null;
  onboardingId: string | null;
  joinedAt: string;
  removedAt: string | null;
  removedReason: string | null;
  sib: { number: string | null; expiresOn: string | null } | null;
  producerCodes: { carrier: string; producerCode: string; status: string }[];
  portfolio: { policies: number; customers: number; annualPremium: Money[] } | null;
  self: boolean;
};
type Terms = {
  split?: string;
  commissions?: Record<string, string>;
  regime: string;
  nit?: string;
};
type LogEntry = {
  id: number;
  at: string;
  actor: { memberId: string; email: string | null };
  action: string;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
};
export type TeamProfile = TeamPerson & {
  phone: string | null;
  dpi: string | null;
  nit: string | null;
  supervisor: { memberId: string; name: string } | null;
  production: Money[];
  terms: {
    current: (Terms & { effectiveFrom: string | null }) | null;
    versions: { effectiveFrom: string; terms: Terms; applied: boolean }[];
  };
  log: LogEntry[];
};
type Team = { identity: 'OK' | 'UNAVAILABLE'; today: string; members: TeamPerson[] };

/** The roles an agency gives, in the person's words, with what each does. */
export const TEAM_ROLES: [role: string, help: string][] = [
  ['agency_director', 'Dirige la agencia: todo, incluidos los términos de los agentes.'],
  ['agency_admin', 'Asistencia administrativa: opera la agencia y administra el equipo.'],
  ['agency_manager', 'Gerencia: lee la cartera de todos los agentes y aprueba códigos.'],
  ['cashier', 'Caja: cobra y cuadra la caja.'],
  ['collections_officer', 'Cobros: da seguimiento a recibos vencidos y en mora.'],
  ['evaluator', 'Evaluación: revisa los antecedentes de los candidatos.'],
  ['training_manager', 'Formación: prepara los cursos y sus exámenes.'],
  ['agent', 'Agente: vende y ve su propia cartera; necesita su código de agente.'],
];
export const STATUS_LABEL: Record<string, [state: string, label: string]> = {
  ACTIVE: ['ok', 'Activo'],
  INVITED: ['warn', 'Invitado'],
  REMOVED: ['bad', 'Baja'],
};
const CODE_STATUS: Record<string, [state: string, label: string]> = {
  PROVISIONAL: ['warn', 'Provisional'],
  DEFINITIVE: ['ok', 'Definitivo'],
  REVOKED: ['bad', 'Revocado'],
};
const REGIME: Record<string, string> = {
  SIMPLIFIED: 'Pequeño contribuyente simplificado',
  PROFITS: 'Sobre utilidades',
  SMALL_TAXPAYER: 'Pequeño contribuyente',
};
const ACTION: Record<string, string> = {
  INVITE: 'Invitó',
  ROLE_CHANGE: 'Cambió el rol',
  REMOVE: 'Dio de baja',
  PROFILE_UPDATE: 'Editó el perfil',
  TERMS_VERSION: 'Pactó términos',
};
const CHIPS: [status: string, label: string][] = [
  ['', 'Todos'],
  ['ACTIVE', 'Activos'],
  ['INVITED', 'Invitados'],
  ['REMOVED', 'De baja'],
];

const roleName = (r: string) => ROLE[r] ?? r;
/** Writes the team: the direction and the administration (the API and Cerbos decide). */
export const writesTeam = () => has('agency_director', 'agency_admin');
/** Reads the team: they, and the agency manager in reading. */
export const readsTeam = () => writesTeam() || has('agency_manager');
const agreesTerms = () => has('agency_director');

/** What the screen shows above the list: a form, a confirmation, a profile or why it refused. */
type Panel =
  | { kind: 'invite' }
  | { kind: 'role'; memberId: string }
  | { kind: 'remove'; memberId: string }
  | { kind: 'profile'; memberId: string; editing?: 'profile' | 'terms' }
  | { kind: 'refused'; title: string; text: string };
let panel: Panel | null = null;
const filter = { role: '', status: 'ACTIVE' };
let shown: TeamPerson[] = [];
let today = '';

/** Back to the plain list (a new session). */
export function resetEquipo() {
  panel = null;
  shown = [];
  filter.role = '';
  filter.status = 'ACTIVE';
}

const personOf = (id: string) => shown.find((p) => p.memberId === id);
const money = (ms: Money[] | undefined) => (ms?.length ? ms.map((m) => fmt(m)).join(' + ') : '—');
const roleOptions = (selected: string, only = TEAM_ROLES.map(([r]) => r)) =>
  TEAM_ROLES.filter(([r]) => only.includes(r)).map(([r]) => ({
    value: r,
    label: roleName(r),
    selected: r === selected,
  }));
/** A director is named only by a director (the API refuses it to anyone else too). */
const grantable = () =>
  TEAM_ROLES.map(([r]) => r).filter((r) => r !== 'agency_director' || has('agency_director'));

function inviteHtml() {
  return `<form id="team-invite-form" class="card" data-component="team-invite" novalidate>
    <div class="card-head"><h2>Invitar a una persona al equipo</h2></div>
    ${REQUIRED_HINT}
    <div class="form-grid"><fieldset><legend>La persona</legend>
      ${field('Nombre', textControl({ id: 'team-name', maxlength: 120, autocomplete: 'name' }), { required: true })}
      ${field('Correo', textControl({ id: 'team-email', type: 'email', maxlength: 254, autocomplete: 'email', hint: 'Le enviamos la invitación para entrar.' }), { required: true })}
      ${field('Rol', selectControl({ id: 'team-role', label: 'Rol' }, roleOptions('cashier', grantable())), { required: true })}
      ${field('Código de agente', textControl({ id: 'team-agent', maxlength: 40, hint: 'Solo para el rol Agente: el código con que vende («ana»). Si viene del reclutamiento, invítalo desde Onboarding.' }))}
    </fieldset></div>
    <p class="note info">${icon('shield')}<span>${TEAM_ROLES.map(([r, h]) => `<b>${esc(roleName(r))}</b>: ${esc(h)}`).join(' ')}</span></p>
    <div class="form-foot"><span class="muted small">Stytch le envía el correo; aparece como «Invitado» hasta que entre.</span>
      <span class="spacer"></span>
      <button type="button" class="btn" data-team-cancel>Cancelar</button>
      <button type="submit" class="btn primary">Invitar</button></div>
  </form>`;
}

function roleHtml(p: TeamPerson) {
  return `<form id="team-role-form" class="card" data-component="team-role" data-member="${esc(p.memberId)}" novalidate>
    <div class="card-head"><h2 id="team-panel-title">Cambiar el rol de ${esc(p.name)}</h2></div>
    <div class="form-grid"><fieldset><legend>Rol nuevo</legend>
      ${field('Rol', selectControl({ id: 'team-new-role', label: 'Rol' }, roleOptions(p.role, grantable())), { required: true })}
      ${field('Código de agente', textControl({ id: 'team-new-agent', maxlength: 40, value: p.agentId ?? '', hint: 'Solo si pasa a ser agente.' }))}
    </fieldset></div>
    <p class="note info">${icon('alert')}<span>Hoy es ${esc(roleName(p.role))}. El cambio vale desde su próxima sesión y queda en la bitácora del equipo.</span></p>
    <div class="form-foot"><span class="spacer"></span>
      <button type="button" class="btn" data-team-cancel>Cancelar</button>
      <button type="submit" class="btn primary">Cambiar el rol</button></div>
  </form>`;
}

function removeHtml(p: TeamPerson) {
  const onboarding = p.onboardingId
    ? ' También termina su nombramiento en el onboarding y su código de productor queda revocado.'
    : '';
  return `<form id="team-remove-form" class="card" role="alertdialog" aria-labelledby="team-panel-title" aria-describedby="team-remove-text" data-component="team-remove" data-member="${esc(p.memberId)}" novalidate>
    <div class="card-head"><h2 id="team-panel-title">¿Dar de baja a ${esc(p.name)}?</h2></div>
    <p class="note bad" id="team-remove-text">${icon('alert')}<span>${esc(p.email)} ya no podrá entrar a Nubi desde este momento.${esc(onboarding)} Sus pólizas siguen siendo suyas y la bitácora guarda quién la dio y por qué.</span></p>
    <div class="form-grid"><fieldset><legend>La baja</legend>
      ${field('Motivo', textControl({ id: 'team-reason', maxlength: 500, hint: 'Por ejemplo: Renuncia.' }), { required: true })}
    </fieldset></div>
    <div class="form-foot"><span class="spacer"></span>
      <button type="button" class="btn" data-team-cancel>Cancelar</button>
      <button type="submit" class="btn danger">Sí, dar de baja</button></div>
  </form>`;
}

const refusedHtml = (title: string, text: string) =>
  `<section class="card" role="alert" data-component="team-refused">
    <div class="card-head"><h2 id="team-panel-title">${esc(title)}</h2></div>
    <p class="note bad">${icon('alert')}<span>${esc(text)}</span></p>
    <div class="form-foot"><span class="spacer"></span><button type="button" class="btn" data-team-cancel>Entendido</button></div>
  </section>`;

const termsText = (t: Terms) => {
  const lines = Object.entries(t.commissions ?? {});
  const share = lines.length
    ? lines.map(([l, v]) => `${lineName(l)} ${v} %`).join(' · ')
    : t.split
      ? `${t.split} % de la comisión`
      : '—';
  return `${share} · ${REGIME[t.regime] ?? t.regime}${t.nit ? ` · NIT ${t.nit}` : ''}`;
};
const codesHtml = (p: TeamPerson) =>
  p.producerCodes.length
    ? p.producerCodes
        .map((c) => {
          const [state, label] = CODE_STATUS[c.status] ?? ['warn', c.status];
          return `<span class="mono">${esc(c.producerCode)}</span> <span class="muted small">${esc(c.carrier.toUpperCase())}</span> ${pill(state, label)}`;
        })
        .join('<br>')
    : '<span class="muted">—</span>';
const describe = (e: LogEntry) => {
  const a = e.after ?? {};
  const b = e.before ?? {};
  switch (e.action) {
    case 'ROLE_CHANGE':
      return `${roleName(String(b.role ?? ''))} → ${roleName(String(a.role ?? ''))}`;
    case 'REMOVE':
      return `Motivo: ${String(a.reason ?? '—')}`;
    case 'INVITE':
      return `Como ${roleName(String(a.role ?? ''))}`;
    case 'TERMS_VERSION':
      return `Desde ${date(String(a.effectiveFrom ?? ''))}`;
    default:
      return 'Contacto, identidad o supervisor';
  }
};

async function profileHtml(memberId: string, editing?: 'profile' | 'terms') {
  const p = await api<TeamProfile>('GET', `/v1/team/${encodeURIComponent(memberId)}`);
  const writes = writesTeam() && p.status !== 'REMOVED';
  const isAgent = p.role === 'agent' && !!p.agentId;
  const [state, label] = STATUS_LABEL[p.status] ?? ['warn', p.status];
  const dl = (rows: [string, string][]) =>
    `<dl class="party-data">${rows.map(([k, v]) => `<div><dt>${esc(k)}</dt><dd>${v}</dd></div>`).join('')}</dl>`;
  const supervisors = shown.filter(
    (x) => x.status !== 'REMOVED' && x.role !== 'agent' && x.memberId !== p.memberId,
  );
  const editProfile =
    editing === 'profile'
      ? `<form id="team-profile-form" class="team-subform" data-member="${esc(p.memberId)}" novalidate>
        <div class="form-grid"><fieldset><legend>Contacto e identidad</legend>
          ${field('Teléfono', textControl({ id: 'team-phone', maxlength: 20, value: p.phone ?? '', autocomplete: 'tel' }))}
          ${field('DPI', textControl({ id: 'team-dpi', maxlength: 15, inputmode: 'numeric', hint: p.dpi ? `Guardado: ${p.dpi}. Escríbelo completo solo para cambiarlo.` : '13 dígitos.' }))}
          ${field('NIT', textControl({ id: 'team-nit', maxlength: 15, hint: p.nit ? `Guardado: ${p.nit}. Escríbelo completo solo para cambiarlo.` : 'Con su dígito verificador.' }))}
          ${
            isAgent
              ? field(
                  'Supervisor',
                  selectControl({ id: 'team-supervisor', label: 'Supervisor' }, [
                    { value: '', label: 'Sin supervisor', selected: !p.supervisorId },
                    ...supervisors.map((s) => ({
                      value: s.memberId,
                      label: `${s.name} · ${roleName(s.role)}`,
                      selected: s.memberId === p.supervisorId,
                    })),
                  ]),
                )
              : ''
          }
        </fieldset></div>
        <div class="form-foot"><span class="spacer"></span><button type="button" class="btn" data-team-edit="">Cancelar</button><button type="submit" class="btn primary">Guardar</button></div>
      </form>`
      : '';
  const editTerms =
    editing === 'terms'
      ? `<form id="team-terms-form" class="team-subform" data-member="${esc(p.memberId)}" novalidate>
        ${REQUIRED_HINT}
        <div class="form-grid"><fieldset><legend>Términos nuevos</legend>
          ${field('Rige desde', textControl({ id: 'terms-from', type: 'date', min: today, value: today, hint: 'Hoy o después; nunca hacia atrás.' }), { required: true })}
          ${field('Comisión del agente (%)', textControl({ id: 'terms-split', maxlength: 6, inputmode: 'decimal', value: p.terms.current?.split ?? '', hint: 'Parte de la comisión de la agencia que recibe.' }), { required: true })}
          ${field(
            'Régimen fiscal',
            selectControl(
              { id: 'terms-regime', label: 'Régimen fiscal' },
              Object.entries(REGIME).map(([value, l]) => ({
                value,
                label: l,
                selected: value === (p.terms.current?.regime ?? 'SIMPLIFIED'),
              })),
            ),
            { required: true },
          )}
          ${field('NIT para sus facturas', textControl({ id: 'terms-nit', maxlength: 15, value: p.terms.current?.nit ?? '' }))}
        </fieldset></div>
        <div class="form-foot"><span class="muted small">Lo ya liquidado no cambia: la versión nueva rige desde su fecha.</span><span class="spacer"></span><button type="button" class="btn" data-team-edit="">Cancelar</button><button type="submit" class="btn primary">Pactar</button></div>
      </form>`
      : '';
  const portfolio = p.portfolio
    ? dl([
        ['Clientes', String(p.portfolio.customers)],
        ['Pólizas vigentes', String(p.portfolio.policies)],
        ['Prima anual de su cartera', esc(money(p.portfolio.annualPremium))],
        ['Producción del año', esc(money(p.production))],
      ])
    : '';
  const versions = p.terms.versions.length
    ? `<ul class="party-list">${p.terms.versions
        .map(
          (v) =>
            `<li>Desde ${esc(date(v.effectiveFrom))}: ${esc(termsText(v.terms))} ${v.applied ? pill('ok', 'Vigente o aplicada') : pill('info', 'Por entrar en vigencia')}</li>`,
        )
        .join('')}</ul>`
    : '<p class="team-text muted small">Sin versiones pactadas en Nubi todavía.</p>';
  const log = p.log.length
    ? `<ul class="party-list">${p.log
        .map(
          (e) =>
            `<li><b>${esc(ACTION[e.action] ?? e.action)}</b> · ${esc(describe(e))} <span class="muted small">${esc(when(e.at))} · ${esc(e.actor.email ?? e.actor.memberId)}</span></li>`,
        )
        .join('')}</ul>`
    : '<p class="team-text muted small">Sin cambios registrados.</p>';
  return `<section class="card" aria-labelledby="team-panel-title" data-component="team-profile">
    <div class="card-head"><h2 id="team-panel-title">${esc(p.name)}</h2> ${pill(state, label)}<span class="spacer"></span>
      ${writes && !editing ? `<button type="button" class="btn sm" data-team-edit="profile">${icon('file')}Editar perfil</button>` : ''}
      ${writes ? `<button type="button" class="btn sm" data-team-role="${esc(p.memberId)}" aria-label="Cambiar el rol de ${esc(p.name)}">Cambiar rol</button>` : ''}
      ${writes && !p.self ? `<button type="button" class="btn sm danger" data-team-remove="${esc(p.memberId)}" aria-label="Dar de baja a ${esc(p.name)}">Dar de baja</button>` : ''}
      ${p.onboardingId ? `<button type="button" class="btn sm" data-go="onboarding">${icon('users')}Ver su onboarding</button>` : ''}
      <button type="button" class="btn sm" data-team-cancel>Cerrar</button></div>
    ${dl([
      ['Rol', esc(roleName(p.role))],
      ['Correo', esc(p.email)],
      ['Teléfono', esc(p.phone ?? '—')],
      ['DPI', p.dpi ? `<span class="mono">${esc(p.dpi)}</span>` : '—'],
      ['NIT', p.nit ? `<span class="mono">${esc(p.nit)}</span>` : '—'],
      ...(isAgent
        ? ([
            ['Código de agente', `<span class="mono">${esc(p.agentId)}</span>`],
            ['Supervisor', esc(p.supervisor?.name ?? 'Sin supervisor')],
          ] as [string, string][])
        : []),
      ['Alta', esc(date(p.joinedAt))],
      ['Baja', esc(p.removedAt ? `${date(p.removedAt)} · ${p.removedReason ?? ''}` : '—')],
      ...(p.sib
        ? ([
            ['Código SIB', p.sib.number ? `<span class="mono">${esc(p.sib.number)}</span>` : '—'],
            ['Autorización SIB vigente hasta', esc(p.sib.expiresOn ? date(p.sib.expiresOn) : '—')],
          ] as [string, string][])
        : []),
    ])}
    ${editProfile}
    ${
      isAgent
        ? `<h3 class="team-sub">Códigos de productor</h3><p class="team-text">${codesHtml(p)}</p>
    <h3 class="team-sub">Cartera</h3>${portfolio}
    <h3 class="team-sub">Términos pactados</h3>
    <p class="team-text">${p.terms.current ? `${esc(termsText(p.terms.current))}${p.terms.current.effectiveFrom ? ` <span class="muted small">desde ${esc(date(p.terms.current.effectiveFrom))}</span>` : ''}` : '<span class="muted">Sin términos registrados.</span>'}
      ${agreesTerms() && p.status !== 'REMOVED' && editing !== 'terms' ? ` <button type="button" class="btn sm" data-team-edit="terms">${icon('receipt')}Pactar términos nuevos</button>` : ''}</p>
    ${editTerms}${versions}`
        : ''
    }
    <h3 class="team-sub">Bitácora</h3>${log}
  </section>`;
}

export async function equipo() {
  const description = writesTeam()
    ? 'Las personas de la agencia: su rol, sus códigos, su estado y su cartera; invita, cambia roles y da de baja.'
    : 'Las personas de la agencia: su rol, sus códigos, su estado y su cartera.';
  if (!readsTeam())
    return `${pageHead('Equipo de la agencia', description)}${empty('lock', 'Esta sección es de la dirección de la agencia', 'Tu rol no administra al equipo.', '', 2)}`;
  const inviteButton = writesTeam()
    ? `<button type="button" class="btn primary" data-team-invite>${icon('users')}Invitar</button>`
    : '';
  const head = pageHead(
    'Equipo de la agencia',
    description,
    panel?.kind === 'invite' ? '' : inviteButton,
  );
  const team = await api<Team>('GET', '/v1/team');
  shown = team.members;
  today = team.today;
  const p = panel;
  const target =
    p && p.kind !== 'invite' && p.kind !== 'refused' ? personOf(p.memberId) : undefined;
  const top =
    p?.kind === 'invite'
      ? inviteHtml()
      : p?.kind === 'refused'
        ? refusedHtml(p.title, p.text)
        : p?.kind === 'role' && target
          ? roleHtml(target)
          : p?.kind === 'remove' && target
            ? removeHtml(target)
            : p?.kind === 'profile' && target
              ? await profileHtml(target.memberId, p.editing)
              : '';
  const unavailable =
    team.identity === 'UNAVAILABLE'
      ? `<p class="note bad" role="status">${icon('alert')}<span>Stytch no respondió: ves lo que Nubi sabe del equipo. Invitar, cambiar roles y dar de baja esperan a que responda.</span></p>`
      : '';
  const count = (s: string) => shown.filter((x) => !s || x.status === s).length;
  const agents = shown.filter((x) => x.agentId && x.status !== 'REMOVED');
  const premium = new Map<string, number>();
  for (const a of agents)
    for (const m of a.portfolio?.annualPremium ?? [])
      premium.set(m.currency, (premium.get(m.currency) ?? 0) + m.amountMinor);
  const kpis = `<section class="kpis" aria-label="Indicadores del equipo">
    <div class="card kpi"><span class="kpi-label">${icon('users')}Activos</span><span class="kpi-value">${count('ACTIVE')}</span><span class="kpi-foot">${agents.length} ${agents.length === 1 ? 'agente' : 'agentes'} con cartera</span></div>
    <div class="card kpi"><span class="kpi-label">${icon('bell')}Invitados</span><span class="kpi-value">${count('INVITED')}</span><span class="kpi-foot">Aún no entran a Nubi</span></div>
    <div class="card kpi"><span class="kpi-label">${icon('policy')}Pólizas vigentes</span><span class="kpi-value">${agents.reduce((n, a) => n + (a.portfolio?.policies ?? 0), 0)}</span><span class="kpi-foot">De los agentes activos</span></div>
    <div class="card kpi"><span class="kpi-label">${icon('wallet')}Prima anual</span><span class="kpi-value">${esc(money([...premium].map(([currency, amountMinor]) => ({ currency, amountMinor }) as Money)))}</span><span class="kpi-foot">Cartera vigente del equipo</span></div>
  </section>`;
  const filters = `<div class="toolbar">
    <div class="chip-filters" role="group" aria-label="Personas por estado">${CHIPS.map(
      ([s, l]) =>
        `<button type="button" class="chip-filter" data-team-chip="${s}" aria-pressed="${filter.status === s}">${esc(l)}<span class="count">${count(s)}</span></button>`,
    ).join('')}</div>
    ${field('Rol', selectControl({ id: 'team-filter-role', label: 'Rol' }, [{ value: '', label: 'Todos los roles', selected: !filter.role }, ...roleOptions(filter.role)]))}
  </div>`;
  const list = shown.filter(
    (x) =>
      (!filter.status || x.status === filter.status) && (!filter.role || x.role === filter.role),
  );
  const rows = list
    .map((x) => {
      const [state, label] = STATUS_LABEL[x.status] ?? ['warn', x.status];
      return `<tr data-team-member="${esc(x.memberId)}"><td><button type="button" class="link" data-team-profile="${esc(x.memberId)}" aria-label="Ver el perfil de ${esc(x.name)}"><strong>${esc(x.name)}</strong></button>${x.self ? ' <span class="muted small">(tú)</span>' : ''}<br><span class="muted small">${esc(x.email)}</span></td>
        <td>${esc(roleName(x.role))}${x.agentId ? `<br><span class="mono small">${esc(x.agentId)}</span>` : ''}</td>
        <td>${x.sib?.number ? `<span class="mono">${esc(x.sib.number)}</span>` : '<span class="muted">—</span>'}</td>
        <td>${codesHtml(x)}</td>
        <td>${pill(state, label)}${x.removedAt ? `<br><span class="muted small">${esc(date(x.removedAt))}</span>` : ''}</td>
        <td class="num">${x.portfolio ? x.portfolio.policies : '—'}</td>
        <td class="num">${x.portfolio?.annualPremium.length ? x.portfolio.annualPremium.map((m) => esc(fmt(m))).join('<br>') : '—'}</td>
</tr>`;
    })
    .join('');
  const table = list.length
    ? `<div class="table-wrap" data-component="table"><table class="table"><caption class="sr-only">Personas del equipo de la agencia</caption>
    <thead><tr><th scope="col">Persona</th><th scope="col">Rol</th><th scope="col">Código SIB</th><th scope="col">Códigos de productor</th><th scope="col">Estado</th><th scope="col" class="num">Pólizas vigentes</th><th scope="col" class="num">Prima anual</th></tr></thead>
    <tbody>${rows}</tbody></table></div>`
    : empty('users', 'Nadie con ese rol y estado', 'Cambia los filtros para ver a más personas.');
  return `${head}${unavailable}${top}${kpis}<section class="card" data-component="agency-team"><div class="card-head"><h2>Personas</h2><span class="spacer"></span><span class="muted small">${list.length} de ${shown.length}</span></div>
    ${filters}${table}</section>`;
}

/** Paints the screen again and puts the focus where the person continues. */
async function repaint(focus?: string) {
  await render(current, { quiet: true });
  if (focus) document.querySelector<HTMLElement>(focus)?.focus();
}
const open = (p: Panel | null, focus?: string) => {
  panel = p;
  void repaint(focus);
};

/** The API's refusals that keep the agency directed, explained on the screen and in a toast. */
const REFUSALS: Record<string, [title: string, text: string]> = {
  LAST_DIRECTOR: [
    'La agencia se quedaría sin dirección',
    'La agencia necesita al menos una directora activa. Invita a otra persona como directora y espera a que entre; después podrás cambiar este rol o dar esta baja.',
  ],
  CANNOT_REMOVE_SELF: [
    'No puedes darte de baja',
    'Pide a otra persona de la dirección que lo haga, si de verdad dejas la agencia.',
  ],
  DIRECTOR_ONLY: ['Solo la dirección', 'Solo una directora nombra, cambia o da de baja a otra.'],
  ONBOARDING_IN_PROGRESS: [
    'Todavía está en su onboarding',
    'Cierra su onboarding desde «Onboarding»: ahí se rechaza al candidato.',
  ],
  ONBOARDING_AGENT: [
    'Es un agente del onboarding',
    'Su rol y sus códigos los lleva su onboarding; para que deje de serlo, dale de baja.',
  ],
};
async function guarded(run: () => Promise<string>): Promise<string> {
  try {
    return await run();
  } catch (e) {
    const refusal = e instanceof ApiError ? REFUSALS[e.code] : undefined;
    if (refusal) {
      panel = { kind: 'refused', title: refusal[0], text: refusal[1] };
      await repaint('#team-panel-title');
    }
    if (e instanceof ApiError && e.code === 'IDENTITY_UNAVAILABLE')
      throw new InputError(
        'Stytch no respondió: no se cambió nada. Intenta de nuevo en un momento.',
      );
    if (e instanceof ApiError && e.code === 'AGENT_CODE_TAKEN')
      throw new InputError('Ese código de agente ya es de otra persona del equipo.', 'team-agent');
    if (e instanceof ApiError && e.code === 'TERMS_RETROACTIVE')
      throw new InputError(
        'Los términos rigen desde hoy o después, nunca hacia atrás.',
        'terms-from',
      );
    throw e;
  }
}

const value = (id: string) => ($(`#${id}`) as HTMLInputElement | null)?.value?.trim() ?? '';
const memberOf = (ev: Event) => (ev.target as HTMLElement).dataset.member ?? '';
const submitButton = (ev: Event) =>
  (ev.target as HTMLFormElement).querySelector<HTMLElement>('[type="submit"]');

export const actions: Actions = {
  click: {
    teamInvite: () => open({ kind: 'invite' }, '#team-name'),
    teamCancel: () => open(null, '[data-team-invite]'),
    teamProfile: (_b, d) =>
      open({ kind: 'profile', memberId: d.teamProfile ?? '' }, '#team-panel-title'),
    teamRole: (_b, d) => open({ kind: 'role', memberId: d.teamRole ?? '' }, '#team-panel-title'),
    teamRemove: (_b, d) =>
      open({ kind: 'remove', memberId: d.teamRemove ?? '' }, '#team-panel-title'),
    teamEdit: (_b, d) => {
      if (panel?.kind !== 'profile') return;
      const editing = d.teamEdit === 'profile' || d.teamEdit === 'terms' ? d.teamEdit : undefined;
      open(
        { kind: 'profile', memberId: panel.memberId, ...(editing ? { editing } : {}) },
        editing === 'terms' ? '#terms-from' : editing ? '#team-phone' : '#team-panel-title',
      );
    },
    teamChip: (_b, d) => {
      filter.status = d.teamChip ?? '';
      void repaint(`[data-team-chip="${filter.status}"]`);
    },
  },
  change: {
    'team-filter-role': (control) => {
      filter.role = control.value;
      void repaint('#team-filter-role');
    },
  },
  submit: {
    'team-invite-form': (ev) => {
      ev.preventDefault();
      void act(submitButton(ev), 'Invitando…', () =>
        guarded(async () => {
          const name = textInput('#team-name', 'el nombre');
          const email = textInput('#team-email', 'el correo').toLowerCase();
          if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email))
            throw new InputError('Escribe un correo válido.', 'team-email');
          const role = value('team-role') || 'cashier';
          const agentId = value('team-agent').toLowerCase();
          if (role === 'agent' && !agentId)
            throw new InputError('Escribe el código de agente con que vende.', 'team-agent');
          const out = await api<{ invited: boolean }>('POST', '/v1/team', {
            name,
            email,
            role,
            ...(role === 'agent' ? { agentId } : {}),
          });
          panel = null;
          await repaint();
          return out.invited
            ? `Invitamos a ${name} como ${roleName(role)}.`
            : `${email} ya es parte del equipo: no se envió otra invitación.`;
        }),
      );
    },
    'team-role-form': (ev) => {
      ev.preventDefault();
      const p = personOf(memberOf(ev));
      if (!p) return;
      void act(submitButton(ev), 'Cambiando el rol…', () =>
        guarded(async () => {
          const role = value('team-new-role');
          const agentId = value('team-new-agent').toLowerCase();
          if (role === 'agent' && !agentId)
            throw new InputError('Escribe el código de agente con que vende.', 'team-new-agent');
          const out = await api<{ changed: boolean }>(
            'PUT',
            `/v1/team/${encodeURIComponent(p.memberId)}/role`,
            { role, ...(role === 'agent' ? { agentId } : {}) },
          );
          panel = null;
          await repaint();
          return out.changed
            ? `${p.name} ahora es ${roleName(role)}.`
            : `${p.name} ya era ${roleName(role)}.`;
        }),
      );
    },
    'team-remove-form': (ev) => {
      ev.preventDefault();
      const p = personOf(memberOf(ev));
      if (!p) return;
      void act(submitButton(ev), 'Dando de baja…', () =>
        guarded(async () => {
          const reason = textInput('#team-reason', 'el motivo');
          if (reason.length < 3)
            throw new InputError('Escribe el motivo de la baja.', 'team-reason');
          await api('POST', `/v1/team/${encodeURIComponent(p.memberId)}/removal`, { reason });
          panel = null;
          await repaint();
          return `${p.name} quedó de baja: ya no puede entrar a Nubi.`;
        }),
      );
    },
    'team-profile-form': (ev) => {
      ev.preventDefault();
      const memberId = memberOf(ev);
      void act(submitButton(ev), 'Guardando…', () =>
        guarded(async () => {
          const phone = value('team-phone');
          const dpi = value('team-dpi').replace(/\s+/g, '');
          const nit = value('team-nit').toUpperCase();
          if (dpi && !/^\d{13}$/.test(dpi))
            throw new InputError('El DPI tiene 13 dígitos.', 'team-dpi');
          if (nit && !/^\d{1,12}-?[0-9K]$/.test(nit))
            throw new InputError('Escribe el NIT con su dígito verificador.', 'team-nit');
          const supervisor = $('#team-supervisor') ? value('team-supervisor') : undefined;
          await api('PUT', `/v1/team/${encodeURIComponent(memberId)}/profile`, {
            phone: phone || null,
            ...(dpi ? { dpi } : {}),
            ...(nit ? { nit } : {}),
            ...(supervisor !== undefined ? { supervisorId: supervisor || null } : {}),
          });
          panel = { kind: 'profile', memberId };
          await repaint('#team-panel-title');
          return 'Perfil guardado.';
        }),
      );
    },
    'team-terms-form': (ev) => {
      ev.preventDefault();
      const memberId = memberOf(ev);
      void act(submitButton(ev), 'Pactando…', () =>
        guarded(async () => {
          const effectiveFrom = value('terms-from');
          const split = value('terms-split');
          if (!/^\d{4}-\d{2}-\d{2}$/.test(effectiveFrom))
            throw new InputError('Elige desde cuándo rigen.', 'terms-from');
          if (!/^\d{1,3}(\.\d{1,2})?$/.test(split) || Number(split) <= 0 || Number(split) > 100)
            throw new InputError('La comisión es un porcentaje entre 0 y 100.', 'terms-split');
          const nit = value('terms-nit').toUpperCase();
          const out = await api<{ inForce: boolean }>(
            'POST',
            `/v1/team/${encodeURIComponent(memberId)}/terms`,
            {
              effectiveFrom,
              split,
              regime: value('terms-regime') || 'SIMPLIFIED',
              ...(nit ? { nit } : {}),
            },
          );
          panel = { kind: 'profile', memberId };
          await repaint('#team-panel-title');
          return out.inForce
            ? 'Términos pactados: rigen desde hoy.'
            : `Términos pactados: rigen desde el ${date(effectiveFrom)}.`;
        }),
      );
    },
  },
};
